import React, { useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { adminService, type AdminUserRecord, type SalesforceUserResult } from '../../services/adminService';
import { ApiError } from '../../services/apiClient';

const ORG_DOMAIN = 'bluwave.net';

function formatDate(ts: number | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function fullName(user: AdminUserRecord): string {
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || '—';
}

function parseApiErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const body = JSON.parse(err.bodyText);
      if (typeof body.message === 'string') return body.message;
    } catch {
      // fall through
    }
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}

// ── Add User Modal ────────────────────────────────────────────────────────────

type ModalStep = 'input' | 'confirm';

interface AddUserModalProps {
  onClose: () => void;
  onCreated: (user: AdminUserRecord) => void;
}

function AddUserModal({ onClose, onCreated }: AddUserModalProps) {
  const { getToken } = useAuthContext();

  const [step, setStep] = useState<ModalStep>('input');
  const [emailPrefix, setEmailPrefix] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [sfUser, setSfUser] = useState<SalesforceUserResult | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    const prefix = emailPrefix.trim().toLowerCase();
    if (!prefix) return;

    setLookupLoading(true);
    setLookupError(null);

    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const result = await adminService.lookupSalesforceUser(token, prefix);
      setSfUser(result);
      setStep('confirm');
    } catch (err) {
      setLookupError(parseApiErrorMessage(err));
    } finally {
      setLookupLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!sfUser) return;
    setCreateLoading(true);
    setCreateError(null);

    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const newUser = await adminService.createUser(token, {
        emailPrefix: emailPrefix.trim().toLowerCase(),
        isAdmin,
        salesforceId: sfUser.salesforceId,
        salesforceUsername: sfUser.salesforceUsername,
        salesforceProfileId: sfUser.salesforceProfileId,
        firstName: sfUser.firstName,
        lastName: sfUser.lastName,
      });
      onCreated(newUser);
    } catch (err) {
      setCreateError(parseApiErrorMessage(err));
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-lg bg-white shadow-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Add User from Salesforce</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        {step === 'input' && (
          <form onSubmit={handleLookup}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <div className="flex items-center rounded-md border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-[#29446D] focus-within:border-[#29446D]">
              <input
                ref={inputRef}
                type="text"
                value={emailPrefix}
                onChange={(e) => {
                  setEmailPrefix(e.target.value);
                  setLookupError(null);
                }}
                placeholder="afarmer"
                className="flex-1 px-3 py-2 text-sm outline-none bg-white"
                disabled={lookupLoading}
              />
              <span className="px-3 py-2 text-sm text-gray-500 bg-gray-50 border-l border-gray-300 whitespace-nowrap">
                @{ORG_DOMAIN}
              </span>
            </div>

            {lookupError && (
              <p className="mt-2 text-sm text-red-600">{lookupError}</p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!emailPrefix.trim() || lookupLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-[#29446D] rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#1f3356]"
              >
                {lookupLoading ? 'Looking up…' : 'Next'}
              </button>
            </div>
          </form>
        )}

        {step === 'confirm' && sfUser && (
          <div>
            <div className="rounded-md bg-gray-50 border border-gray-200 px-4 py-3 space-y-1 text-sm">
              <div>
                <span className="text-gray-500">Name: </span>
                <span className="font-medium text-gray-900">
                  {[sfUser.firstName, sfUser.lastName].filter(Boolean).join(' ') || '—'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Email: </span>
                <span className="font-medium text-gray-900">
                  {emailPrefix.trim().toLowerCase()}@{ORG_DOMAIN}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Salesforce username: </span>
                <span className="font-mono text-gray-700">{sfUser.salesforceUsername}</span>
              </div>
            </div>

            <label className="flex items-center gap-2 mt-4 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isAdmin}
                onChange={(e) => setIsAdmin(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-[#29446D]"
              />
              <span className="text-sm font-medium text-gray-700">Grant admin access</span>
            </label>

            {createError && (
              <p className="mt-3 text-sm text-red-600">{createError}</p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setStep('input'); setSfUser(null); setCreateError(null); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={createLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-[#29446D] rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#1f3356]"
              >
                {createLoading ? 'Creating…' : 'Create User'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const AdminUsersPage: React.FC = () => {
  const { getToken } = useAuthContext();
  const [users, setUsers] = useState<AdminUserRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const token = await getToken();
        if (!token) throw new Error('Not authenticated');
        const data = await adminService.listUsers(token);
        if (!cancelled) setUsers(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load users');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [getToken]);

  const handleUserCreated = (newUser: AdminUserRecord) => {
    setUsers((prev) => (prev ? [newUser, ...prev] : [newUser]));
    setModalOpen(false);
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-sm text-gray-500 mt-1">All Clerk users and their metadata</p>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#29446D] rounded-md hover:bg-[#1f3356]"
        >
          + Add User
        </button>
      </div>

      {loading && (
        <div className="text-gray-500 text-sm">Loading users…</div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {users && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Name</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Email</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Admin</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Salesforce ID</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Salesforce Username</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Salesforce Profile ID</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Created</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Last Sign In</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                    {fullName(user)}
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {user.primaryEmail ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    {user.isAdmin ? (
                      <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                        Yes
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                        No
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs whitespace-nowrap">
                    {user.salesforceId ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {user.salesforceUsername ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs whitespace-nowrap">
                    {user.salesforceProfileId ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {formatDate(user.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {formatDate(user.lastSignInAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100 bg-gray-50">
            {users.length} user{users.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {modalOpen && (
        <AddUserModal
          onClose={() => setModalOpen(false)}
          onCreated={handleUserCreated}
        />
      )}
    </div>
  );
};

export default AdminUsersPage;
